import { exists } from '@/helpers/utilities';

let AutoSuggest = (({}) => {
class AutoSuggest {
    constructor(node, selector) {
    this.node = node;
    this.selector = selector;
    this.nodeExists = exists(this.node);
    if (!this.nodeExists) return;

    this.debounceTimer = null;
    this.resultsList = null;
    this.container = this.node.parentElement;
    this.parentForm = this.node.closest('form');
    this.listId = `cu-autosuggest-list-${Math.random().toString(36).substr(2, 9)}`;
    
    // Track active visual index instead of shifting document focus
    this.activeIndex = -1; 
    }

    setParentPosition() {
        if (this.container) {
            this.container.style.position = 'relative';
        }
    }

    setupAccessibility() {
        this.node.setAttribute('role', 'combobox');
        this.node.setAttribute('aria-autocomplete', 'list');
        this.node.setAttribute('aria-expanded', 'false');
        this.node.setAttribute('aria-haspopup', 'listbox');
        this.node.setAttribute('aria-controls', this.listId);
        this.node.setAttribute('autocomplete', 'off');
    }

    createResultsElement() {
        if (this.resultsList) return;
        this.resultsList = document.createElement('ul');
        this.resultsList.id = this.listId;
        this.resultsList.className = 'cu-autosuggest-results';
        this.resultsList.setAttribute('role', 'listbox');
        this.resultsList.addEventListener('click', this.onResultClick.bind(this));
        this.node.parentNode.insertBefore(this.resultsList, this.node.nextSibling);
    }

    renderResults(data) {
        if (data.length > 0) {
            this.createResultsElement();
            this.activeIndex = -1; // Reset selection index
            this.resultsList.innerHTML = data
            .map((item, index) => `
                <li class="cu-autosuggest-item" 
                    id="${this.listId}-option-${index}" 
                    role="option" 
                    aria-selected="false">${item}</li>
            `).join('');
            this.node.setAttribute('aria-expanded', 'true');
        } else {
            this.hideResults();
        }
    }

    hideResults() {
        if (this.resultsList) {
            this.resultsList.remove();
            this.resultsList = null;
            this.node.setAttribute('aria-expanded', 'false');
            this.node.removeAttribute('aria-activedescendant');
            this.activeIndex = -1;
        }
    }

    async fetchData(term) {
        try {
            const response = await fetch(window.ajaxurl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ action: 'fetch_keywords', term: term })
            });
            const result = await response.json();
            
            if (result.success && Array.isArray(result.data)) {
            this.renderResults(result.data);
            } else {
            this.hideResults();
            }
        } catch (error) {
            console.error('Autosuggest Error:', error);
            this.hideResults();
        }
    }

    handleInput(e) {
        const term = e.target.value.trim();
        clearTimeout(this.debounceTimer);
        if (term.length < 2) {
            this.hideResults();
            return;
        }
        this.debounceTimer = setTimeout(() => this.fetchData(term), 300);
    }

    onResultClick(e) {
        const itemNode = e.target.closest('.cu-autosuggest-item');
        if (itemNode) {
            this.selectItem(itemNode);
        }
    }

    selectItem(itemNode) {
        this.node.value = itemNode.textContent;
        this.hideResults();
        if (this.parentForm) this.parentForm.submit();
    }

    onDocumentClick(e) {
        if (!this.node.contains(e.target) && (!this.resultsList || !this.resultsList.contains(e.target))) {
            this.hideResults();
        }
    }

    // Handles arrow keys, escape, enter, and focus syncing while focus stays inside the input field
    onInputKeyDown(e) {
    if (!this.resultsList) return;

    const items = this.resultsList.querySelectorAll('.cu-autosuggest-item');
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIndex = (this.activeIndex + 1 >= items.length) ? 0 : this.activeIndex + 1;
        this.updateActiveItem(items);
    } 
    
    else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIndex = (this.activeIndex - 1 < 0) ? items.length - 1 : this.activeIndex - 1;
        this.updateActiveItem(items);
    } 
    
    else if (e.key === 'Enter') {
        if (this.activeIndex > -1) {
            e.preventDefault();
            this.selectItem(items[this.activeIndex]);
        }
    } 
    
    else if (e.key === 'Escape') {
        e.preventDefault();
        this.hideResults();
    }
    
    // Note: "Tab" key is deliberately left unhandled.
    // Allowing default behavior means the popup stays in place or closes naturally 
    // while browser focus proceeds to the Close button instantly.
    }

    updateActiveItem(items) {
    // Remove previous active classes and aria states
    items.forEach((item) => {
        item.classList.remove('active');
        item.setAttribute('aria-selected', 'false');
    });

    const activeItem = items[this.activeIndex];
    if (activeItem) {
        activeItem.classList.add('active');
        activeItem.setAttribute('aria-selected', 'true');
        this.node.setAttribute('aria-activedescendant', activeItem.id);
        
        // Ensure highlighted item is visible in scrolling lists
        activeItem.scrollIntoView({ block: 'nearest' });
    }
    }

    setEventBindings() {
    this.node.addEventListener('input', this.handleInput.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));
    this.node.addEventListener('keydown', this.onInputKeyDown.bind(this));
    
    // Close results instantly if user tabs out of the search bar
    this.node.addEventListener('blur', (e) => {
        // Delay slightly to prevent breaking mouse click selections on items
        setTimeout(() => {
        if (!this.resultsList || !this.resultsList.contains(document.activeElement)) {
            this.hideResults();
        }
        }, 150);
    });
    }

    init() {
    if (!this.nodeExists) return;
    this.setParentPosition();
    this.setupAccessibility();
    this.setEventBindings();
    }
}

return {
    init({ selector }) {
    let nodeList = document.querySelectorAll(selector);
    if (!nodeList.length) return;
    return [...nodeList].map((node) => {
        let module = new AutoSuggest(node, selector);
        module.init();
        return module;
    });
    }
};
})(window);

export default AutoSuggest;
